import { useMutation, useQueryClient } from '@tanstack/react-query';

import { decideProject } from '../api/projects.api';
import { extractRpcCode } from '@/shared/lib/errors';
import { invalidateAfterTransition } from '@/shared/lib/invalidate';
import { queryKeys } from '@/shared/lib/queryKeys';
import type { Id, ProjectDecision } from '@/shared/types/domain';

interface DecideVariables {
  projectId: Id;
  ministryId: Id;
  decision: ProjectDecision;
  comment?: string | null;
}

/**
 * Aprovar, solicitar ajustes ou rejeitar (spec-project-review.md §4.3).
 *
 * `invalidateAfterTransition` é obrigatório (regra 10): além da fila e do
 * calendário, ela agora também limpa `['space-conflicts']` — aprovar o
 * projeto A deixa o alerta do projeto B velho.
 */
export function useDecideProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, decision, comment }: DecideVariables) =>
      decideProject(projectId, decision, comment),
    onSuccess: (_data, variables) =>
      invalidateAfterTransition(queryClient, variables.projectId, variables.ministryId),
    /**
     * **Perder a corrida também é uma transição — só que de outra pessoa**
     * (decisão 26). `decide_project` faz `select … for update` e levanta
     * `already_decided`: nenhuma segunda decisão passa, com ou sem
     * reivindicação. O que faltava era a TELA se corrigir em vez de mandar o
     * usuário recarregar. Invalidar o detalhe e a linha do tempo faz o crachá,
     * a atribuição do cabeçalho e o painel contarem a verdade sozinhos.
     */
    onError: (error, variables) => {
      if (extractRpcCode(error) !== 'already_decided') return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.timeline(variables.projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reviewQueue });
    },
  });
}
